import { useState, useEffect } from 'react';
import {FaThumbsUp, FaChartBar, FaInfoCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import styles from './Respuestas.module.css';
import {useAuth} from '../../../Auth/AuthContext'; 

const Respuestas = ({ testId }) => {
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tipoRespuesta, setTipoRespuesta] = useState('likert');
    const [opcionesActuales, setOpcionesActuales] = useState([]);
    const [plantillasDisponibles, setPlantillasDisponibles] = useState([]);
    const [loadingPlantillas, setLoadingPlantillas] = useState(true);

    useEffect(() => {
        if (testId) {
            fetchTipoRespuesta();
            fetchOpcionesActuales();
            fetchPlantillasDisponibles();
        }
    }, [testId]);

    const fetchTipoRespuesta = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/obtener/${testId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            if (data && data.length > 0) {
                setTipoRespuesta(data[0].te_tipo_respuesta || 'si_no');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchOpcionesActuales = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/opciones-respuestas/seleccion/${testId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setOpcionesActuales(data);
            }
        } catch (err) {
            console.error('Error al obtener opciones actuales:', err);
        }
    };

    const fetchPlantillasDisponibles = async () => {
        try {
            setLoadingPlantillas(true);
            
            const url = `${process.env.REACT_APP_API_URL}/test/plantillas-opciones`; 

            const response = await fetch(url, { 
                method: 'GET', 
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            }); 

            if (response.ok) {
                const plantillas = await response.json();

                const plantillasFiltradas = plantillas.filter(plantilla =>
                    plantilla.pl_tipo === 'likert' || plantilla.pl_tipo === 'si_no'
                );

                const plantillasConOpciones = await Promise.all(
                    plantillasFiltradas.map(async (plantilla) => {
                        const opcionesResponse = await fetch(
                            `${process.env.REACT_APP_API_URL}/test/plantillas-opciones/${plantilla.pl_clave}/opciones`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            }
                        );
                        if (opcionesResponse.ok) {
                            const opciones = await opcionesResponse.json();
                            return {
                                ...plantilla,
                                opciones: opciones
                            };
                        }
                        return plantilla;
                    })
                );

                setPlantillasDisponibles(plantillasConOpciones);
            }
        } catch (err) {
            console.error('Error al obtener plantillas:', err);
        } finally {
            setLoadingPlantillas(false);
        }
    };

const handleAplicarPlantilla = async (plantillaId) => {
    const plantilla = plantillasDisponibles.find(p => p.pl_clave === plantillaId);
    if (!plantilla) {
        showErrorAlert('Plantilla no encontrada');
        return;
    }

    try {
        setSaving(true);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/test/aplicar-plantilla`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`, 
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                testId: parseInt(testId),
                plantillaId: parseInt(plantillaId)
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
        }

        setTipoRespuesta(plantilla.pl_tipo);
        await fetchOpcionesActuales();
        showSuccessAlert(plantilla.pl_nombre);

    } catch (error) {
        console.error('Error completo:', error);
        showErrorAlert(error.message || 'Error de conexión');
    } finally {
        setSaving(false);
    }
};

    const showSuccessAlert = (nombrePlantilla) => {
        Swal.fire({
            title: '¡Plantilla aplicada!',
            text: `Plantilla "${nombrePlantilla}" aplicada correctamente`,
            icon: 'success',
            showConfirmButton: false, 
            customClass: { 
                popup: 'popupbonito', 
            },
            timer: 1000, 
        });
    };

    const showErrorAlert = (errorMessage) => {
        Swal.fire({
            title: 'Error',
            text: `${errorMessage}`,
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#242b57', 
            customClass: { 
                popup: 'popupbonito', 
            },
        });
    };

    const getIconForType = (tipo) => {
        switch (tipo) {
            case 'likert':
                return <FaChartBar />;
            case 'si_no':
                return <FaThumbsUp />;
            default:
                return <FaInfoCircle />;
        }
    };

    const renderPlantillasDisponibles = () => {
        if (loadingPlantillas) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando plantillas...</p>
                </div>
            </div>
        );
        }

        if (plantillasDisponibles.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <h3>No hay plantillas disponibles</h3>
                    <p>Por favor revisa tu conexión.</p>
                </div>
            );
        }

        return (
            <div className={styles.opcionesDisponibles}>
                <h4>Plantillas disponibles:</h4>
                <div className={styles.tiposGrid}>
                    {plantillasDisponibles.map((plantilla, index) => {
                        const isSelected = opcionesActuales.length > 0 &&
                            tipoRespuesta === plantilla.pl_tipo &&
                            opcionesActuales.length === plantilla.opciones?.length;

                        return (
                            <div
                                key={plantilla.pl_clave}
                                className={`${styles.tipoCard} ${isSelected ? styles.selected : ''}`}
                                onClick={() => handleAplicarPlantilla(plantilla.pl_clave)}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className={styles.tipoHeader}>
                                    <div className={styles.tipoIcon}>
                                        {getIconForType(plantilla.pl_tipo)}
                                    </div>
                                    <h5>{plantilla.pl_nombre}</h5>
                                </div>

                                <div className={styles.tipoDescripcion}>
                                    <p>{plantilla.pl_descripcion}</p>
                                </div>

                                {plantilla.opciones && (
                                    <div className={`${styles.opcionesPreview} ${plantilla.pl_tipo === 'si_no' ? styles.siNo : styles.likert}`}>
                                        {plantilla.pl_tipo === 'si_no' ? (
                                            plantilla.opciones.map(opcion => (
                                                <span
                                                    key={opcion.pd_valor}
                                                    className={styles.opcionPreview}
                                                    data-valor={opcion.pd_valor}
                                                >
                                                    {opcion.pd_etiqueta}
                                                </span>
                                            ))
                                        ) : plantilla.pl_tipo === 'likert' ? (
                                            <>
                                                <div className={styles.likertScale}>
                                                    <div className={styles.likertPoints}>
                                                        {plantilla.opciones.map(opcion => (
                                                            <span
                                                                key={opcion.pd_valor}
                                                                className={styles.opcionPreview}
                                                                data-tooltip={opcion.pd_etiqueta}
                                                                style={{ backgroundColor: opcion.pd_color }}
                                                            >
                                                                {opcion.pd_valor}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className={styles.likertLabels}>
                                                    <span className={styles.likertLabel}>
                                                        {plantilla.opciones[0]?.pd_etiqueta}
                                                    </span>
                                                    <span className={styles.likertLabel}>
                                                        {plantilla.opciones[plantilla.opciones.length - 1]?.pd_etiqueta}
                                                    </span>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando respuestas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContainer}> 
            <p className={styles.loadingText}> Error al cargar datos :( </p>
            </div>
        </div>
        );
    }

    if (saving) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Guardando plantilla...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.respuestasContainer}>
            <div className={styles.respuestasHeader}>
                <div className={styles.headerInfo}>
                    <div>
                        <h3>Configuración de Respuestas</h3>
                        <p>Selecciona una plantilla para definir el formato de las respuestas en este test.</p>
                        <p>La opción elegida determinará cómo se presentarán las respuestas. Este formato se aplicará de forma uniforme a todas las preguntas del test (no es posible combinar respuestas del tipo "Sí/No" con escala Likert).</p>
                    </div>
                </div>
            </div>

            {renderPlantillasDisponibles()}
        </div>
    );
};

export default Respuestas; 
